import * as Sentry from '@sentry/react';
import { ScriptixDescendant } from '@utils/types/editors/transcripts/transcript.ts';
import { Editor, Transforms } from 'slate';
import { ReactEditor } from 'slate-react';

/** Rename the speaker on exactly one Slate node.
 *
 * The row the user clicked is a specific Slate element; use Slate's own
 * path-lookup to find it, so a rename never leaks to any other paragraph.
 *
 * Falls back to (start, stop) matching only when the element isn't attached
 * to the editor (e.g. a stale reference during a re-render). Even then, the
 * fallback matches a SINGLE node — the first one with the same timings —
 * because splitting a paragraph can leave two paragraphs sharing a timestamp
 * boundary, and renaming "paragraphs that happen to share this stop" would
 * surprise the user. */
export const changeSpeaker = (
  value: string,
  element: ScriptixDescendant,
  editor: Editor
) => {
  try {
    let path: number[] | null = null;

    // Primary path: ask Slate for the element's exact location. This is the
    // one node the user clicked, no ambiguity.
    try {
      path = ReactEditor.findPath(editor as ReactEditor, element);
    } catch {
      path = null;
    }

    // Fallback: locate the first top-level node with the same timings. One
    // match, not all — see the doc comment above.
    if (!path) {
      for (let i = 0; i < editor.children.length; i++) {
        const child = editor.children[i] as ScriptixDescendant;
        if (child.start === element.start && child.stop === element.stop) {
          path = [i];
          break;
        }
      }
    }

    if (!path) return;

    Editor.withoutNormalizing(editor, () => {
      Transforms.setNodes(
        editor,
        { speaker: value } as Partial<ScriptixDescendant>,
        { at: path! }
      );
    });
  } catch (error) {
    Sentry.addBreadcrumb({
      category: 'editor',
      message: 'changeSpeaker failed',
      level: 'error',
      data: { value, error: String(error) }
    });
  }
};
